import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';
import { useState, useEffect } from 'react';
import Layout from './components/Layout';
import { SettingsPage } from './pages/Settings';
import {
  UserProfile,
  ApplicationSettings,
  AdministrationDashboard,
  PreCalChecklist,
  EquipmentAssessmentConfig,
  CustomerAcknowledgmentConfig,
  GeneralSettings,
  StationDevices
} from './pages/Settings/sections';
import { AllowedEvents } from './pages/Settings/sections/Administration/AllowedEvents';
import { ActionCommands } from './pages/Settings/sections/Administration/ActionCommands';
import NotFoundPage from './pages/NotFoundPage';
import ProtectedRoute from './components/ProtectedRoute';
import WorkOrderListPage from './pages/Work/WorkOrderListPage';
import WorkOrderProfilePage from './pages/Work/WorkOrderProfilePage';
import UpdateNotification from './components/UpdateNotification';
import CertificateJobListener from './components/CertificateJobListener';
import { swManager } from './services/serviceWorkerManager';
import { BenchCalibrationPage } from '@pages/Work/BenchCalibrationPage';
import {
  CalibrationRunPage,
  CalibrationRedirect
} from '@pages/Work/CalibrationRunPage';
import { CustomerReviewPage } from '@features/customerReview';
import { HistoryPage } from '@pages/History';
import ScriptsListPage from '@features/scripts/pages/ScriptsListPage';
import ScriptProfilePage from '@features/scripts/pages/ScriptProfilePage';
import {
  useStationDevicesQuery,
  useSyncStationDeviceToBff
} from '@api/bff/stationDevices';
import { useMqttStore } from '@stores/mqttStore';

const App = () => {
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);

  useSyncStationDeviceToBff();
  const { activeStationId } = useStationDevicesQuery();
  const setStationId = useMqttStore(state => state.setStationId);

  useEffect(() => {
    if (activeStationId) {
      setStationId(activeStationId);
    }
  }, [activeStationId, setStationId]);

  useEffect(() => {
    swManager.setUpdateCallback(() => setShowUpdateNotification(true));
  }, []);

  const handleUpdate = () => {
    setShowUpdateNotification(false);
    swManager.skipWaitingAndReload();
  };

  const handleCloseNotification = () => {
    setShowUpdateNotification(false);
  };

  LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

  return (
    <Box sx={{ display: 'flex' }}>
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/work" replace />} />
          <Route path="work">
            <Route index element={<WorkOrderListPage />} />
            <Route path=":jobNumber" element={<WorkOrderProfilePage />} />
            <Route path=":jobNumber/review" element={<CustomerReviewPage />} />
            <Route
              path=":jobNumber/calibration"
              element={<CalibrationRunPage />}
            />
            <Route
              path=":jobNumber/execution"
              element={<CalibrationRedirect />}
            />
            <Route
              path=":jobNumber/:woDetailGlobalId/execution"
              element={<CalibrationRedirect />}
            />
            <Route
              path=":jobNumber/:woDetailGlobalId"
              element={<CalibrationRedirect />}
            />
          </Route>
          <Route path="scripts">
            <Route index element={<ScriptsListPage />} />
            <Route path=":scriptId" element={<ScriptProfilePage />} />
          </Route>
          <Route path="history/:dcRuntimeId" element={<HistoryPage />} />
          <Route
            path="calibration/:dcRuntimeId"
            element={<BenchCalibrationPage />}
          />
          <Route path="settings" element={<SettingsPage />}>
            <Route path="profile" element={<UserProfile />} />
            <Route path="application" element={<ApplicationSettings />} />
            <Route path="administration">
              <Route index element={<AdministrationDashboard />} />
              <Route path="precal-checklist" element={<PreCalChecklist />} />
              <Route path="allowed-events" element={<AllowedEvents />} />
              <Route
                path="equipment-assessment"
                element={<EquipmentAssessmentConfig />}
              />
              <Route
                path="customer-acknowledgment"
                element={<CustomerAcknowledgmentConfig />}
              />
              <Route path="general-settings" element={<GeneralSettings />} />
              <Route path="station-devices" element={<StationDevices />} />
              <Route path="action-commands" element={<ActionCommands />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>

      <CertificateJobListener />
      <UpdateNotification
        open={showUpdateNotification}
        onUpdate={handleUpdate}
        onClose={handleCloseNotification}
      />
    </Box>
  );
};

export default App;
